/* eslint-disable react/prop-types */
import Button from 'components/base/Button';
import ExcelButton from 'components/functional-buttons/ExcelButton';
import DatePicker from 'components/base/DatePicker';
import dayjs from 'dayjs';
import { Form } from 'react-bootstrap';
import Icon from 'components/icon';
import { UilCalendarAlt } from '@iconscout/react-unicons';
import { useEffect, useRef, useState } from 'react';
import fetchData from 'services/fetchData';

const checkMark = url => {
  if (url && typeof url === 'string') {
    if (!url.includes('?')) {
      return `${url}?`;
    }
    return url;
  }
  return url;
};

const GetDataByDateRange = ({
  loading,
  setLoading,
  setFetchedData,
  setGlobalFilter,
  url,
  staticUrl = null,
  exportName,
  onDateRangeChange = () => {},
  startRange = 2,
  onRegisterRefresh = () => {}
}) => {
  const [dateRange, setDateRange] = useState([]);
  const [fetchDate, setFetchDate] = useState([]);
  const [oldDateRange, setOldDateRange] = useState([]);
  const fp = useRef(null);

  useEffect(() => {
    const calculatePreviousWeek = () => {
      const startDate = dayjs().subtract(startRange, 'day');
      const endDate = dayjs();
      return [startDate.toDate(), endDate.toDate()];
    };
    setDateRange(calculatePreviousWeek());
    onDateRangeChange(calculatePreviousWeek());
  }, []);

  const handleOpenCalendar = () => {
    if (fp.current) {
      fp.current.flatpickr.open();
    }
  };

  const fetchRequest = async () => {
    const startDate = dayjs(dateRange[0]).format('DD-MM-YYYY');
    const endDate = dayjs(dateRange[1]).format('DD-MM-YYYY');

    if (startDate !== fetchDate[0] || endDate !== fetchDate[1]) {
      setLoading(true);
      const response = await fetchData(
        `${checkMark(staticUrl) || checkMark(url)}start_date=${startDate}&end_date=${endDate}`,
        { isStatic: !!staticUrl }
      );
      if (response.status) {
        setFetchedData(response.data);
        setFetchDate([startDate, endDate]);
      } else {
        console.log('Failed to get data :', response.message);
      }
      setLoading(false);
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (dateRange?.length === 2) {
      fetchRequest();
    }
  }, [dateRange]);

  const refreshData = async () => {
    if (dateRange?.length === 2) {
      setLoading(true);
      setGlobalFilter(undefined);

      const startDate = dayjs(dateRange[0]).format('DD-MM-YYYY');
      const endDate = dayjs(dateRange[1]).format('DD-MM-YYYY');
      const response = await fetchData(
        `${checkMark(staticUrl) || checkMark(url)}start_date=${startDate}&end_date=${endDate}`,
        { isStatic: !!staticUrl }
      );
      if (response.status) {
        setFetchedData(response.data);
      } else {
        setFetchedData([]);
        console.log('Failed to get data :', response.message);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (onRegisterRefresh) {
      onRegisterRefresh(refreshData);
    }
  }, [onRegisterRefresh, refreshData]);

  const handleOnCloseDate = dates => {
    if (dates?.length === 2) {
      setDateRange(dates);
      onDateRangeChange(dates);
    } else {
      setDateRange(oldDateRange);
      onDateRangeChange(oldDateRange);
    }
  };

  return (
    <div className="d-flex justify-content-end gap-1 ms-auto">
      <ExcelButton prefix={exportName} />
      <Button
        className="p-0 px-2 position-relative"
        variant="phoenix-primary"
        title="Refresh table"
        disabled={dateRange?.length !== 2 || loading}
        onClick={refreshData}
      >
        <Icon icon={'heroicons-outline:refresh'} />
      </Button>
      <div className="position-relative my-auto flatpickr-input-container ms-auto">
        <UilCalendarAlt
          className="position-absolute flatpickr-icon text-700"
          size={16}
        />
        <Form.Control
          onClick={handleOpenCalendar}
          disabled={loading}
          readonly="readonly"
          className="ps-6 cursor-pointer"
          type="text"
          value={`${dayjs(dateRange[0]).format('DD-MM-YYYY')} To ${dayjs(
            dateRange[1]
          ).format('DD-MM-YYYY')}`}
        />
        <DatePicker
          inputRef={fp}
          hideIcon={true}
          style={{
            position: 'absolute',
            top: '-30px',
            right: 0,
            zIndex: -1,
            opacity: 0
          }}
          value={dateRange}
          onClose={handleOnCloseDate}
          onOpen={() => setOldDateRange(dateRange)}
          options={{
            mode: 'range',
            maxDate: dayjs().add(1, 'day').toDate(),
            dateFormat: 'Y-m-d',
            disable: [
              function (date) {
                // Disable future dates and yesterday's date
                return date.getTime() > dayjs().toDate();
              }
            ]
          }}
          placeholder="Select date"
        />
      </div>
    </div>
  );
};

export default GetDataByDateRange;
